import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import api, { formatApiError } from '../lib/api';
import { Phone, EnvelopeSimple, Clock, MapPin, PaperPlaneTilt, CheckCircle } from '@phosphor-icons/react';

const SUBJECTS = ['Reservation', 'Annulation', 'Facturation', 'Reclamation', 'Suggestion', 'Autre'];

export default function Contact() {
  const [form, setForm] = useState({ fullName: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const mapRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      if (window.google?.maps && mapRef.current && !mapRef.current._map) {
        mapRef.current._map = new window.google.maps.Map(mapRef.current, {
          center: { lat: 48.8566, lng: 2.3522 },
          zoom: 12,
          styles: [
            { elementType: 'geometry', stylers: [{ color: '#1a1a1a' }] },
            { elementType: 'labels.text.stroke', stylers: [{ color: '#0d0d0d' }] },
            { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
            { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2a2720' }] },
            { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e1626' }] },
          ],
          disableDefaultUI: true,
          zoomControl: true,
        });
        new window.google.maps.Marker({
          position: { lat: 48.8566, lng: 2.3522 },
          map: mapRef.current._map,
          title: 'DestiDriving - Paris',
        });
        clearInterval(interval);
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.post('/contact', form);
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-desti-gold mb-3">Contactez-nous</p>
            <h1 className="font-heading text-4xl sm:text-5xl text-white">Nous sommes a votre ecoute</h1>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              {sent ? (
                <div className="bg-desti-surface border border-desti-gold/20 p-8 text-center" data-testid="contact-success">
                  <CheckCircle size={48} weight="duotone" className="text-desti-gold mx-auto mb-4" />
                  <h3 className="font-heading text-2xl text-white mb-3">Message envoye !</h3>
                  <p className="text-sm text-desti-text">Nous vous repondrons dans les meilleurs delais.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Nom complet *</label>
                    <input type="text" required value={form.fullName} onChange={e => setForm(p => ({ ...p, fullName: e.target.value }))}
                      className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-desti-gold transition-colors"
                      placeholder="Votre nom" data-testid="contact-name-input" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Email *</label>
                      <input type="email" required value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                        className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-desti-gold transition-colors"
                        placeholder="votre@email.fr" data-testid="contact-email-input" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Telephone</label>
                      <input type="tel" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                        className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-desti-gold transition-colors"
                        placeholder="06 XX XX XX XX" data-testid="contact-phone-input" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Sujet *</label>
                    <select required value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                      className="w-full bg-desti-black border-b border-white/20 px-3 py-3 text-white text-sm focus:outline-none focus:border-desti-gold transition-colors appearance-none"
                      data-testid="contact-subject-select">
                      <option value="">Selectionnez un sujet</option>
                      {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-desti-gold mb-2 block">Message * (min. 10 caracteres)</label>
                    <textarea required minLength={10} rows={5} value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                      className="w-full bg-desti-black border border-white/20 px-3 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-desti-gold transition-colors resize-none"
                      placeholder="Votre message..." data-testid="contact-message-input" />
                  </div>
                  {error && <p className="text-red-400 text-sm" data-testid="contact-error">{error}</p>}
                  <button type="submit" disabled={loading}
                    className="w-full bg-desti-gold text-desti-black font-medium py-4 tracking-wide hover:bg-desti-gold-hover transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="contact-submit-btn">
                    {loading ? 'Envoi...' : 'Envoyer'} <PaperPlaneTilt size={16} weight="bold" />
                  </button>
                </form>
              )}
            </div>

            <div className="space-y-8">
              <div ref={mapRef} className="w-full h-64 lg:h-80 bg-desti-surface border border-white/5" data-testid="contact-map" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <Phone size={20} weight="duotone" className="text-desti-gold mt-1" />
                  <div><p className="text-sm text-white">06 35 13 31 33</p><p className="text-xs text-desti-text mt-1">Service client 24/7</p></div>
                </div>
                <div className="flex items-start gap-3">
                  <EnvelopeSimple size={20} weight="duotone" className="text-desti-gold mt-1" />
                  <div><p className="text-sm text-white">contact@destidriving.fr</p><p className="text-xs text-desti-text mt-1">Reponse sous 24h</p></div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock size={20} weight="duotone" className="text-desti-gold mt-1" />
                  <div><p className="text-sm text-white">Service 24h/24</p><p className="text-xs text-desti-text mt-1">Admin: Lun-Ven 9h-18h</p></div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin size={20} weight="duotone" className="text-desti-gold mt-1" />
                  <div><p className="text-sm text-white">Paris & Ile-de-France</p><p className="text-xs text-desti-text mt-1">+ Aeroports & Gares</p></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
